import { Alert, Button, Col, Modal, Row, Space, Typography, message } from "antd";
import ActionLogsView from "../components/ActionLogsView";
import { SearchQuery } from "../code/search-query/search-query";
import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Guid } from "guid-typescript";
import Loader from "../controls/Loader";
import { getActionLogsExport } from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

function ActionLogsPage() {
  const permissions = useSelector(authorizeSelectors.permissions);
  const location = useLocation();
  // Arrived from a user profile's View Logs (PD-01 §4.3). The scope now lives on the URL query so a
  // hard refresh or shared link keeps the filter — the earlier state-only wiring quietly dropped the
  // scope on F5 and left the banner-less full log in its place.
  const [searchParams, setSearchParams] = useSearchParams();
  const navState = location.state as {
    userUid?: string;
    userName?: string;
  } | null;
  const urlUserUid = searchParams.get("userUid") ?? undefined;
  const urlUserName = searchParams.get("userName") ?? undefined;

  const [modal, contextHolder] = Modal.useModal();
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  // Clearable: the banner says WHY the list is short, and the button takes the operator to the full log
  // without making them find the filter and empty it by hand.
  const [scopedUserUid, setScopedUserUid] = useState<string | undefined>(
    urlUserUid ?? navState?.userUid,
  );
  const [scopedUserName, setScopedUserName] = useState<string | undefined>(
    urlUserName ?? navState?.userName,
  );
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });

  // Events

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading Action Logs export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getActionLogsExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Action Logs
        </Typography.Title>
        <Space className="flex flex-row">
          <Button
            type="primary"
            disabled={!permissions.can("ActionLogs", "view")}
            onClick={() => onDownloadExport()}
          >
            Export to Excel
          </Button>
        </Space>
      </Row>
      {scopedUserUid && (
        <Row className="px-4 pb-2">
          <Alert
            className="w-full"
            type="info"
            showIcon
            message={`Showing activity for ${scopedUserName ?? "one user"}`}
            action={
              <Button
                size="small"
                onClick={() => {
                  setScopedUserUid(undefined);
                  setScopedUserName(undefined);
                  // Clear the URL params so a shared link no longer re-pins the filter.
                  setSearchParams({});
                }}
              >
                Clear
              </Button>
            }
          />
        </Row>
      )}
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        {/* Keyed on the scope so clearing it remounts the grid with an empty filter form, rather than
            leaving the old `userUid` sitting in a field the banner no longer explains. */}
        <ActionLogsView
          key={scopedUserUid ?? "all"}
          userUid={scopedUserUid}
          trigger={triggerView}
          onSearch={onSearch}
        />
      </Col>
      {contextHolder}
    </Col>
  );
}

export default ActionLogsPage;
